import { useMemo } from "react";

import { getTzDayjs, toISO } from "@m7-health/shared-utils";

import { useListSchedulesQuery } from "@/api";
import { CustomDatePicker } from "@/common/components/DeprecatedDatePicker";
import { useCurrentTimezone, useMapBy } from "@/common/hooks";
import { getTzFormattedDate } from "@/common/utils/dates";

import { TShiftEditorProps } from "../types";

export const ShiftEditorDatePicker = (props: TShiftEditorProps) => {
  const { staffShift, updateStaffShift, unitOfShift, staffDetails } = props;
  // use timezone of unit of shift, NOT facility timezone
  const timezone = useCurrentTimezone(unitOfShift.id);
  const shiftDate = getTzFormattedDate(staffShift.date, timezone);

  // Unit selector
  const availableUnitsIds = useMapBy(staffDetails.user.rosters, "unitId");
  const { data: schedules } = useListSchedulesQuery(
    { unitIds: availableUnitsIds },
    { skip: availableUnitsIds.length === 0 },
  );
  const currentScheduleOfShift = useMemo(
    () =>
      schedules?.find(
        ({ unitId, startDay, endDay }) =>
          unitId === unitOfShift.id && startDay <= shiftDate && shiftDate <= endDay,
      ),
    [schedules, unitOfShift.id, shiftDate],
  );

  return (
    <CustomDatePicker
      name="shiftDate"
      label="Date"
      value={getTzDayjs(staffShift.date, timezone)}
      onChange={(newDate) => {
        if (newDate) {
          updateStaffShift({
            ...staffShift,
            date: toISO(newDate),
          });
        }
      }}
      timezone={timezone}
      shouldDisableDate={(date) => {
        // if there is no schedule, disable all dates
        if (!currentScheduleOfShift) return true;
        const yyyyMmDdStr = date?.format("YYYY-MM-DD");
        // if date is not a valid date, disable it
        if (!yyyyMmDdStr) return true;
        return (
          yyyyMmDdStr < currentScheduleOfShift.startDay ||
          yyyyMmDdStr > currentScheduleOfShift.endDay
        );
      }}
    />
  );
};
